import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import api from '../lib/api';
import { fixImageUrl } from '../config';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import {
  MapPin,
  Globe,
  Building,
  Users,
  Calendar,
  Briefcase,
  Star,
  ArrowLeft,
  Facebook,
  Linkedin,
  Twitter,
  Instagram,
  Heart,
  Clock,
  TrendingUp,
  Award,
  CheckCircle2,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import SEO from '../components/SEO';

const EmployerProfile = ({ profile }) => {
  const { employer_id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [employer, setEmployer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchEmployerProfile();
  }, [employer_id]);

  const fetchEmployerProfile = async () => {
    try {
      setLoading(true);

      // Fetch employer profile
      const profileResponse = await api.get(`/api/employers/${employer_id}`);
      setEmployer(profileResponse.data.employer);

      // Fetch employer jobs
      const jobsResponse = await api.get(`/api/employers/${employer_id}/jobs?status=open`);
      setJobs(jobsResponse.data.jobs || []);

      // Fetch employer reviews
      const reviewsResponse = await api.get(`/api/employers/${employer_id}/reviews?limit=10`);
      setReviews(reviewsResponse.data.reviews || []);

    } catch (error) {
      console.error('Failed to fetch employer profile:', error);
      toast.error('Failed to load company profile');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleJobClick = (job) => {
    navigate(`/jobs/${job.slug || job.id}`);
  };

  const socialMediaIcons = {
    facebook: <Facebook className="w-4 h-4" />,
    linkedin: <Linkedin className="w-4 h-4" />,
    twitter: <Twitter className="w-4 h-4" />,
    instagram: <Instagram className="w-4 h-4" />
  };

  if (loading) {
    return (
      <div className="min-h-screen-safe bg-background">
        <TopBar profile={profile} />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="spinner" />
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  if (!employer) {
    return null;
  }

  const stats = employer.stats || {};
  const companyPhotos = employer.company_photos || [];
  const benefits = employer.company_benefits || [];
  const socialLinks = employer.social_media_links || {};

  return (
    <div className="min-h-screen-safe bg-background">
      <SEO
        title={`${employer.business_name || employer.name} - Company Profile`}
        description={employer.business_description || `View jobs and company information for ${employer.business_name || employer.name}`}
      />
      <TopBar profile={profile} />

      <main className="container max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Banner/Header Section */}
        <Card className="card-enhanced mb-6 overflow-hidden">
          {employer.banner_url && (
            <div
              className="w-full h-48 bg-cover bg-center"
              style={{ backgroundImage: `url(${fixImageUrl(employer.banner_url)})` }}
            />
          )}
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Company Logo */}
              <Avatar className={`w-24 h-24 border-4 border-background ${employer.banner_url ? '-mt-12 md:-mt-16' : ''}`}>
                <AvatarImage src={fixImageUrl(employer.avatar_url)} alt={employer.business_name} />
                <AvatarFallback className="text-2xl font-bold">
                  {(employer.business_name || employer.name || 'C')[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>

              {/* Company Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h1 className="text-2xl sm:text-3xl font-bold heading-primary">
                        {employer.business_name || employer.name}
                      </h1>
                      {employer.verified && (
                        <Badge className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold shadow-md border-teal-400 border">
                          <Shield size={14} className="mr-1" />
                          True Boholano
                        </Badge>
                      )}
                      {stats.avg_rating >= 4.5 && stats.review_count >= 3 && (
                        <span className="badge-top-rated">
                          <Star className="w-3 h-3 fill-white" />
                          {t('profile.topRated') || 'Top Rated'}
                        </span>
                      )}
                    </div>
                    {employer.industry && (
                      <p className="text-muted-foreground mb-2">{employer.industry}</p>
                    )}
                    {employer.town && (
                      <div className="flex items-center text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4 mr-1" />
                        {employer.town}, Bohol
                      </div>
                    )}
                  </div>

                  {/* Rating */}
                  {stats.review_count > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold">{stats.avg_rating}</span>
                      <span className="text-sm text-muted-foreground">
                        ({stats.review_count} reviews)
                      </span>
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="flex flex-wrap gap-4 mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="w-4 h-4 text-primary" />
                    <span className="font-semibold">{stats.active_jobs_count}</span>
                    <span className="text-muted-foreground">Active Jobs</span>
                  </div>
                  {employer.number_of_employees && !employer.is_private_household && (
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">{employer.number_of_employees} employees</span>
                    </div>
                  )}
                  {employer.year_founded && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">Founded {employer.year_founded}</span>
                    </div>
                  )}
                </div>

                {/* Social Links */}
                {Object.keys(socialLinks).length > 0 && (
                  <div className="flex gap-2 mt-4">
                    {Object.entries(socialLinks).map(([platform, url]) => (
                      url && (
                        <Button
                          key={platform}
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(url, '_blank')}
                        >
                          {socialMediaIcons[platform]}
                        </Button>
                      )
                    ))}
                    {employer.website && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(employer.website, '_blank')}
                      >
                        <Globe className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="overview" className="flex-1">Overview</TabsTrigger>
            <TabsTrigger value="jobs" className="flex-1">
              Jobs ({stats.active_jobs_count || 0})
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex-1">
              Reviews ({stats.review_count || 0})
            </TabsTrigger>
            {companyPhotos.length > 0 && (
              <TabsTrigger value="photos" className="flex-1">Photos</TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* About Us */}
            {employer.business_description && (
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-5 h-5 text-primary" />
                    About Us
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-body whitespace-pre-line">{employer.business_description}</p>
                </CardContent>
              </Card>
            )}

            {/* Why Work Here */}
            {employer.why_work_here && (
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Why Work Here?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(employer.why_work_here || '') }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Company Culture */}
            {employer.company_culture && (
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="w-5 h-5 text-primary" />
                    Company Culture
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-body whitespace-pre-line">{employer.company_culture}</p>
                </CardContent>
              </Card>
            )}

            {/* Benefits */}
            {benefits.length > 0 && (
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    Benefits & Perks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex gap-3 p-3 bg-secondary/30 rounded-lg">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">{benefit.title}</h4>
                          {benefit.description && (
                            <p className="text-sm text-muted-foreground">{benefit.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            {jobs.length === 0 ? (
              <Card className="card-enhanced">
                <CardContent className="py-12 text-center">
                  <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No active job openings at this time</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {jobs.map((job) => (
                  <Card
                    key={job.id}
                    className="card-enhanced cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleJobClick(job)}
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-2">{job.title}</h3>
                          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </div>
                            {job.salary_min && job.salary_max && (
                              <div className="flex items-center gap-1">
                                <span>₱{job.salary_min.toLocaleString()} - ₱{job.salary_max.toLocaleString()}</span>
                              </div>
                            )}
                            {job.category && (
                              <Badge variant="secondary">{job.category.name}</Badge>
                            )}
                          </div>
                        </div>
                        {job.featured && (
                          <Badge className="badge-featured">Featured</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            {reviews.length === 0 ? (
              <Card className="card-enhanced">
                <CardContent className="py-12 text-center">
                  <Star className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No reviews yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <Card key={review.id} className="card-enhanced">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Avatar>
                          <AvatarImage src={fixImageUrl(review.from_user?.avatar_url)} />
                          <AvatarFallback>
                            {((review.from_user?.role === 'employer' || review.from_user?.role === 'owner') ? (review.from_user?.is_private_household ? 'Private Household' : (review.from_user?.business_name || 'Employer')) : (review.from_user?.name || 'U'))[0].toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-semibold">{(review.from_user?.role === 'employer' || review.from_user?.role === 'owner') ? (review.from_user?.is_private_household ? 'Private Household' : (review.from_user?.business_name || 'Employer')) : (review.from_user?.name || 'Anonymous')}</p>
                              <p className="text-sm text-muted-foreground">
                                {review.from_user?.town && `${review.from_user.town} • `}
                                {new Date(review.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              <span className="font-semibold">{review.rating}</span>
                            </div>
                          </div>
                          {review.comment && (
                            <p className="text-sm text-body">{review.comment}</p>
                          )}
                          {review.context && (
                            <Badge variant="outline" className="mt-2">
                              {review.context}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Photos Tab */}
          {companyPhotos.length > 0 && (
            <TabsContent value="photos">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {companyPhotos.map((photo, index) => (
                  <Card key={index} className="card-enhanced overflow-hidden">
                    <img
                      src={fixImageUrl(photo.url)}
                      alt={photo.caption || `Company photo ${index + 1}`}
                      className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(fixImageUrl(photo.url), '_blank')}
                    />
                    {photo.caption && (
                      <CardContent className="p-3">
                        <p className="text-sm text-muted-foreground">{photo.caption}</p>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>

      <BottomNav profile={profile} />
    </div>
  );
};

export default EmployerProfile;
